/**
 * @documental
 * @export MenuButton
 */
import { FC, FocusEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { Bounds } from "capitalrx-components-2.0/lib//utilities/BoundsObserver/Bounds";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib//utilities/getHtmlPropsFromBaseProps";
import { isDescendentOf } from "capitalrx-components-2.0/lib//utilities/isDescendentOf";
import { withSubComponents } from "capitalrx-components-2.0/lib//utilities/withSubComponents";
import { Button, ButtonProps } from "capitalrx-components-2.0/lib/general/Button";
import { Icon } from "capitalrx-components-2.0/lib/general/Icon";
import { Menu, MenuSubComponents } from "capitalrx-components-2.0/lib/general/Menu";
import { AdjustmentBounds, ElementBounds } from "capitalrx-components-2.0/lib/general/Overlay";
import { useId } from "capitalrx-components-2.0/lib/hooks/useId/useId";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import classNames, { Argument } from "classnames";

import ButtonStyles from "../Button/styles.module.scss";
import styles from "./styles.module.scss";

/**
 * The `MenuButton` component properties.
 */
export interface MenuButtonProps extends Omit<ButtonProps, "onClick" | "aria-expanded"> {
  /**
   * The menu CSS class name.
   */
  readonly menuClassName?: Argument;

  /**
   * The button label.
   */
  readonly label?: ReactNode;

  /**
   * Hides or shows the carret icon.
   */
  readonly caretIcon?: boolean;

  /**
   * Determines whether the caret changes orientation when the menu expands.
   */
  readonly dynamicCaret?: boolean;

  /** Called when the menu is opened. */
  readonly onMenuOpen?: () => void;

  /** Called when the menu is closed. */
  readonly onMenuClose?: () => void;
}

/**
 * A button that when clicked presents a menu of items for selection.
 */
export const MenuButton = withSubComponents<FC<MenuButtonProps>, MenuSubComponents>(
  {
    Item: Menu.Item,
    ComponentItem: Menu.ComponentItem,
    Group: Menu.Group,
    LinkItem: Menu.LinkItem,
  },
  function MenuButton(props) {
    const {
      menuClassName,
      label,
      children,
      caretIcon = true,
      onMenuOpen,
      onMenuClose,
      dynamicCaret = false,
      testId,
      "aria-controls": ariaControlsProp,
      "aria-haspopup": ariaHaspopupProp,
    } = props;
    const [menuVisible, setMenuVisible] = useState(false);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    // id for the menu; if caller supplies aria-controls we use that value, otherwise generate one
    const generatedMenuId = useId();
    const menuId = ariaControlsProp ?? generatedMenuId;

    const htmlBaseProps = getHtmlPropsFromBaseProps(props, {
      className: [
        styles.MenuButtonContainer,
        "menu-button",
        {
          opened: menuVisible,
          focused: menuVisible,
        },
      ],
      testIdPrefix: "menu-button",
    });
    const menuContainerClassName = classNames({
      "items--menu-is-open": menuVisible,
    });

    const clickHandler = useCallback(() => {
      setMenuVisible((current) => !current);
    }, []);

    const menuCloseHandler = useCallback(() => {
      setMenuVisible(false);
      buttonRef.current?.focus();
    }, []);

    const blurHandler = useCallback((ev: FocusEvent<HTMLSpanElement>) => {
      const { currentTarget, relatedTarget } = ev;

      if (relatedTarget && (relatedTarget === currentTarget || isDescendentOf(currentTarget, relatedTarget))) return;

      setMenuVisible(false);
    }, []);

    useEffect(() => {
      if (menuVisible) onMenuOpen?.();
      else onMenuClose?.();
    }, [menuVisible, onMenuClose, onMenuOpen]);

    const menuBoundsChangeHandler = useCallback((elementBounds: ElementBounds, containerBounds: Bounds): AdjustmentBounds => {
      const { bounds } = elementBounds;
      let { top, left } = bounds;
      const { width, height } = bounds;
      const bottom = top + height;

      const { top: containerTop, left: containerLeft, width: containerWidth, height: containerHeight } = containerBounds;
      const containerRight = containerLeft + containerWidth;
      const containerBottom = containerTop + containerHeight;

      if (!buttonRef.current) return bounds;
      const buttonRect = buttonRef.current.getBoundingClientRect();

      const leftDistance = Math.floor(buttonRect.left) - containerLeft;
      const rightDistance = containerRight - buttonRect.right;

      if (rightDistance <= leftDistance) {
        left -= width - buttonRect.width;
      }

      if (containerBottom < bottom) {
        top -= height + buttonRect.height;
      }

      return {
        ...bounds,
        left,
        top,
      };
    }, []);

    return (
      <span {...htmlBaseProps} tabIndex={-1} onBlur={blurHandler}>
        <Button
          {...props}
          aria-haspopup={ariaHaspopupProp ?? "menu"}
          aria-expanded={menuVisible}
          aria-controls={menuId}
          ref={buttonRef}
          className={classNames(styles.MenuButton, { down: menuVisible })}
          testId={testId}
          onClick={clickHandler}
        >
          {label && !caretIcon && <>{label}</>}
          {!label && caretIcon && (
            <Icon
              className={classNames(ButtonStyles.Icon, "dropdown-arrow")}
              name={dynamicCaret && menuVisible ? "chevron-up" : "chevron-down"}
            />
          )}
          {label && caretIcon && (
            <Shelf spacing="small">
              {label}
              {caretIcon && (
                <Icon
                  className={classNames(ButtonStyles.Icon, "dropdown-arrow")}
                  name={dynamicCaret && menuVisible ? "chevron-up" : "chevron-down"}
                />
              )}
            </Shelf>
          )}
        </Button>
        <div className={menuContainerClassName}>
          <Menu
            id={menuId}
            containerClassName={styles.MenuContainer}
            className={[styles.Menu, menuClassName]}
            visible={menuVisible}
            onClose={menuCloseHandler}
            onBoundsChange={menuBoundsChangeHandler}
          >
            {children}
          </Menu>
        </div>
      </span>
    );
  },
);
